import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import {
  BillingAddress,
  NextPayout,
  SheltersNft,
  User,
  UserPreferences,
  UserWallet,
  PayoutHistory,
} from '../../../types/User/User'
import { PayoutMethod, UserPayoutMethod } from '../../../types/Payout'
import { UserSession } from '../../../types/User/UserSession'
import { UserInfoFlow } from '../../../types/User/UserInfoFlow'
import { EsdtToken } from '../../../types/MultiversX/Esdt'

const initialState: UserSliceState = {
  user: null,
  userPreferences: null,
  wallet: null,
  billingAddresses: [],
  userPayoutMethods: [],
  payoutMethods: [],
  nfts: [],
  nftsLoaded: false,
  tokens: [],
  tokensLoaded: false,
  yields: [],
  payoutsHistory: [],
  userSessions: [],
  userCurrentSession: null,
  userInfoFlowStatus: {
    userInfo: false,
    wallet: {
      hasWallet: false,
      isWalletless: false,
    },
    kyc: false,
    billingAddress: false,
  },
}

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    resetUserState: () => initialState,
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload
    },
    setUserPreferences: (state, action: PayloadAction<UserPreferences>) => {
      state.userPreferences = action.payload
    },
    setWallet: (state, action: PayloadAction<UserWallet | null>) => {
      state.wallet = action.payload
    },
    setBillingAddresses: (state, action: PayloadAction<BillingAddress[]>) => {
      state.billingAddresses = action.payload
    },
    setPayoutMethods: (state, action: PayloadAction<PayoutMethod[]>) => {
      state.payoutMethods = action.payload
    },
    setUserPayoutMethods: (
      state,
      action: PayloadAction<UserPayoutMethod[]>,
    ) => {
      state.userPayoutMethods = action.payload
    },
    setNfts: (state, action: PayloadAction<SheltersNft[]>) => {
      state.nfts = action.payload
      state.nftsLoaded = true
    },
    setTokens: (state, action: PayloadAction<any[]>) => {
      state.tokens = action.payload
      state.tokensLoaded = true
    },
    setYields: (state, action: PayloadAction<NextPayout[]>) => {
      state.yields = action.payload
    },
    setPayoutsHistory: (state, action: PayloadAction<PayoutHistory[]>) => {
      state.payoutsHistory = action.payload
    },
    setUserSessions: (state, action: PayloadAction<UserSession[]>) => {
      state.userSessions = action.payload
    },
    setUserCurrentSession: (state, action: PayloadAction<UserSession>) => {
      state.userCurrentSession = action.payload
    },
    updateUserInfoFlowStatus: (
      state,
      action: PayloadAction<Partial<UserInfoFlow>>,
    ) => {
      if (state.userInfoFlowStatus) {
        state.userInfoFlowStatus = {
          ...state.userInfoFlowStatus,
          ...action.payload,
        }
      } else {
        state.userInfoFlowStatus = { ...action.payload } as UserInfoFlow
      }
    },
  },
})

export const {
  resetUserState,
  setUser,
  setUserPreferences,
  setWallet,
  setTokens,
  setBillingAddresses,
  setUserPayoutMethods,
  setPayoutMethods,
  setNfts,
  setYields,
  setPayoutsHistory,
  setUserSessions,
  setUserCurrentSession,
  updateUserInfoFlowStatus,
} = userSlice.actions

/* Types */
export interface UserSliceState {
  user: User | null
  userPreferences: UserPreferences | null
  wallet: UserWallet | null
  billingAddresses: BillingAddress[]
  userPayoutMethods: UserPayoutMethod[]
  payoutMethods: PayoutMethod[]
  nfts: SheltersNft[]
  nftsLoaded: boolean
  tokens: EsdtToken[]
  tokensLoaded: boolean
  yields: NextPayout[]
  payoutsHistory: PayoutHistory[]
  userSessions: UserSession[]
  userCurrentSession: UserSession | null
  userInfoFlowStatus: UserInfoFlow | null
}
